import type {
  DowntimeTimelineElement,
  FacilityTileElement,
  RegionRollupElement,
  ShiftPaceElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

const ATLANTA_TILE: FacilityTileElement = {
  kind: 'facility-tile',
  id: 'tile-atlanta',
  facility: 'Atlanta MRF',
  timeLabel: 'Today, 8:02 AM',
  projected: 330,
  low: 295,
  high: 375,
  plan: 462,
  status: 'red',
  attribution: 'All 4 unplanned-downtime events → Primary Baler #2',
}

const ATLANTA_PACE: ShiftPaceElement = {
  kind: 'shift-pace',
  id: 'pace-atlanta',
  todayLabel: 'Today by 8 AM',
  todayValue: 22,
  comparisonLabel: 'Last Friday, same hour',
  comparisonValue: 66,
  unit: 'tons',
  deltaPercent: -66.7,
  takeaway: 'Same day-of-week, same hour — completely different shift.',
}

const SOUTHEAST_ROLLUP: RegionRollupElement = {
  kind: 'region-rollup',
  id: 'rollup-southeast',
  region: 'Southeast region',
  timeLabel: 'as of 8:05 AM',
  rows: [
    {
      facility: 'Atlanta',
      status: 'red',
      projected: 330,
      plan: 462,
      topRisk: 'Primary Baler #2 — hydraulic seal degradation',
    },
    {
      facility: 'Tampa',
      status: 'orange',
      projected: 410,
      plan: 440,
      topRisk: 'OCC screen — humidity drift',
    },
    {
      facility: 'Birmingham',
      status: 'orange',
      projected: 285,
      plan: 310,
      topRisk: 'Eddy current — calibration drift',
    },
    {
      facility: 'Memphis',
      status: 'green',
      projected: 395,
      plan: 380,
      topRisk: 'On pace',
    },
    {
      facility: 'Nashville',
      status: 'green',
      projected: 360,
      plan: 355,
      topRisk: 'On pace',
    },
    {
      facility: 'Charlotte',
      status: 'green',
      projected: 340,
      plan: 335,
      topRisk: 'On pace',
    },
    {
      facility: 'Conover',
      status: 'green',
      projected: 290,
      plan: 285,
      topRisk: 'On pace',
    },
    {
      facility: 'Raleigh',
      status: 'green',
      projected: 320,
      plan: 315,
      topRisk: 'On pace',
    },
    {
      facility: 'Jacksonville',
      status: 'green',
      projected: 305,
      plan: 300,
      topRisk: 'On pace',
    },
    {
      facility: 'Orlando',
      status: 'green',
      projected: 375,
      plan: 370,
      topRisk: 'On pace',
    },
    {
      facility: 'Miami',
      status: 'green',
      projected: 415,
      plan: 405,
      topRisk: 'On pace',
    },
  ],
}

const ATLANTA_DOWNTIME: DowntimeTimelineElement = {
  kind: 'downtime-timeline',
  id: 'downtime-atlanta',
  totalHours: 1.2,
  typicalHours: 0.12,
  vsTypical: '10× normal',
  windowStart: '06:00',
  windowEnd: '08:00',
  events: [
    { time: '06:18', label: 'Bale jam' },
    { time: '06:52', label: 'Operator inspection' },
    { time: '07:24', label: 'Wire feed misfeed' },
    { time: '07:47', label: 'Hydraulic auto-stop' },
  ],
  attribution: 'All 4 events → Primary Baler #2 (Bollegraaf HBC-110)',
}

/**
 * Act 1 — Unified operational view.
 *
 * One use case, two beats joined by `await-user-prompt`:
 *   1. Atlanta MRF morning briefing (8 AM disaster + Primary Baler #2 callout)
 *   2. Southeast region zoom-out (11-facility roll-up)
 *
 * The demoer types any message between beats; the script ignores its content
 * and continues. Natural prompts: "show me my whole region", "yes", "zoom out".
 */
export const unifiedView: DemoChatScript = {
  id: 'atlanta-today-view',
  name: 'Atlanta Today View',
  description:
    'Act 1. Live operational state at Atlanta MRF this morning, then the regional roll-up — what your Monday morning could look like.',
  triggers: [
    'atlanta',
    'atlanta mrf',
    "what's happening at atlanta",
    'show me atlanta this morning',
    'morning briefing',
    'unified view',
  ],
  events: [
    // ─────────── Beat 1: Atlanta morning briefing ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Reading SCADA, MPower, and OIS feeds for Atlanta MRF...',
    },
    { type: 'thinking-start', delayMs: 250 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'User wants the morning briefing for Atlanta. Pulling end-of-day projection from current pace, comparing against same-DOW last week, ranking unplanned downtime by asset.\n',
    },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Primary Baler #2 (Bollegraaf HBC-110) is owning 100% of unplanned downtime so far this shift. Surfacing the asset attribution alongside the headline.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_scada' },
    { type: 'tool-call', delayMs: 300, name: 'query_mpower' },
    { type: 'tool-call', delayMs: 300, name: 'query_ois' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: 'Good morning. Atlanta MRF, 8:02 AM — two hours into the shift.',
    },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: ATLANTA_TILE,
    },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: ATLANTA_PACE,
    },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: ATLANTA_DOWNTIME,
    },
    {
      type: 'demo-element-add',
      delayMs: 800,
      element: {
        kind: 'text-block',
        id: 'closer-atlanta',
        text: 'The dashboard is pointing at the asset. **Want to see the rest of your Southeast region first, or jump straight into diagnosing Primary Baler #2?**',
      },
    },
    {
      type: 'done',
      delayMs: 400,
      title: 'Atlanta MRF — morning briefing',
    },

    // ─────────── PAUSE — demoer types any prompt to continue ───────────
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: Southeast region zoom-out ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Reading projected EOD across Southeast region facilities...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Zooming out to the regional roll-up. Per-facility projection vs plan, pace status, top equipment risk per site.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_network_state' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Zooming out. Your Southeast region — 11 facilities, ranked by miss-to-plan.',
    },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: SOUTHEAST_ROLLUP,
    },
    {
      type: 'demo-element-add',
      delayMs: 800,
      element: {
        kind: 'text-block',
        id: 'closer-southeast',
        text: 'Atlanta is the only significant miss. Tampa and Birmingham are within operator-action range. **Ready to dig into Primary Baler #2?**',
      },
    },
    {
      type: 'done',
      delayMs: 400,
      title: 'Atlanta Today View',
    },
  ],
  followUps: [
    {
      // Avoid bare 'show' / 'show me' here: matchScript does case-insensitive
      // substring matching and follow-ups take precedence over global
      // triggers, so 'show' would swallow networkPatterns' registered
      // 'show me the network' phrase. Stick to verbs that unambiguously
      // intend predictive maintenance.
      triggers: [
        'yes',
        'yeah',
        'yep',
        'sure',
        'ok',
        'okay',
        'do it',
        'go',
        'go ahead',
        'please',
        'continue',
        'next',
        'diagnose',
        'baler',
        'dig',
        'predict',
        'maintenance',
      ],
      nextScriptId: 'predictive-maintenance',
    },
  ],
}
